import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Full-screen homepage panel that pins to the top while the next section
 * scrolls up and covers it. The outgoing panel dims as it is covered,
 * giving the layered "stacked cards" depth without shrinking the image
 * (which would reveal a visible border around the edges).
 *
 * Panels are plain full-height sections when the visitor prefers reduced
 * motion (handled in CSS via the `stack-panel` utility) so nothing pins.
 */
export function StackPanel({
  children,
  className,
  z = 1,
  rounded = false,
  label,
}: {
  children: ReactNode;
  className?: string;
  /** Paint order: must increase down the page so later sections cover earlier ones. */
  z?: number;
  rounded?: boolean;
  label?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const [range, setRange] = useState<[number, number] | null>(null);
  const { scrollY } = useScroll();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const top = el.offsetTop;
      setRange([top, top + el.offsetHeight]);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const progress = useTransform(scrollY, (value) => {
    if (!range) return 0;
    const [start, end] = range;
    const span = Math.max(1, end - start);
    return Math.min(1, Math.max(0, (value - start) / span));
  });
  const dim = useTransform(progress, [0, 0.5, 1], [0, 0.25, 0.65]);

  return (
    <section
      ref={ref}
      aria-label={label}
      style={{ zIndex: z }}
      className={cn(
        "stack-panel",
        rounded && "rounded-t-[1.75rem] shadow-2xl sm:rounded-t-[2.5rem]",
        className,
      )}
    >
      <div className="relative flex h-full w-full items-center justify-center">{children}</div>
      {reduced ? null : (
        <motion.div
          style={{ opacity: dim }}
          className="pointer-events-none absolute inset-0 bg-black"
          aria-hidden
        />
      )}
    </section>
  );
}
